import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Shield } from "lucide-react";
import type { FieldErrors, SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

import { usePatientInsurances } from "@/hooks/usePatientInsurances";
import { useMultiStepFormStore } from "@/stores";
import type {
  InsuranceInformationFormType,
  SupplementaryPolicyFormType,
} from "@/types";
import { insuranceInformationFormSchema } from "@/types";
import {
  ControlledRadioGroupCard,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/ui/common";
import {
  formatDateForApi,
  INSULIN_DAILY_DOSE,
  YES_NO_OPTIONS,
  YES_NO_RADIO_OPTIONS,
} from "@/utils";
import FormButtons from "../FormButtons";
import { InsuranceCard } from "./InsuranceCard";
import { InsuranceCardsSkeleton } from "./InsuranceCardsSkeleton";
import { InsuranceErrorBanner } from "./InsuranceErrorBanner";

const hasPolicyForm = (
  data: string,
  _errors: FieldErrors<InsuranceInformationFormType>,
): _errors is FieldErrors<SupplementaryPolicyFormType> => {
  return data === YES_NO_OPTIONS.YES;
};

function getPlanType(hmo: string, ppo: string): string | undefined {
  if (hmo === YES_NO_OPTIONS.YES) return "HMO";
  if (ppo === YES_NO_OPTIONS.YES) return "PPO";
  return undefined;
}

export const InsuranceInformationForm = () => {
  const { goToNextFormStep, setMultiStepFormData, multiStepFormData } =
    useMultiStepFormStore();

  const { data: insurances, isLoading } = usePatientInsurances(
    multiStepFormData?.generalInformationFormData?.firstName ?? "",
    multiStepFormData?.generalInformationFormData?.lastName ?? "",
    formatDateForApi(
      multiStepFormData?.generalInformationFormData?.dateOfBirth,
    ) ?? "",
  );

  const {
    handleSubmit,
    control,
    watch,
    register,
    reset,
    formState: { errors },
  } = useForm<InsuranceInformationFormType>({
    defaultValues: { hasSupplementaryPolicy: YES_NO_OPTIONS.NO },
    resolver: zodResolver(insuranceInformationFormSchema),
  });
  const hasSupplementaryPolicy = watch("hasSupplementaryPolicy");
  const dailyInsulinDose = watch("dailyInsulinDose");

  const onSubmit: SubmitHandler<InsuranceInformationFormType> = (data) => {
    const { hasSupplementaryPolicy } = data;

    if (insurances) {
      const baseData = {
        hasSupplementaryPolicy,
        primaryPayer: insurances.primaryPayer,
        primaryPayerMbi: insurances.mbi,
        secondaryPayer: insurances.secondaryPayer,
        secondaryPayerId: insurances.secondaryPayerId,
        hmo: insurances.hmo,
        ppo: insurances.ppo,
        planEffectiveDate: insurances.planEffectiveDate,
        msp: insurances.msp,
      };

      if (
        hasSupplementaryPolicy === YES_NO_OPTIONS.YES &&
        multiStepFormData?.medicalInformationFormData
      ) {
        const {
          supplementaryPolicyName,
          coInsurancePercentage,
          supplementaryPolicyNumber,
        } = data;

        const newMedicalFormData = {
          ...multiStepFormData?.medicalInformationFormData,
          dailyInsulinDose: dailyInsulinDose,
        };

        setMultiStepFormData({
          insuranceInformationFormData: {
            ...baseData,
            supplementaryPolicyName,
            coInsurancePercentage,
            supplementaryPolicyNumber,
          },
          medicalInformationFormData: newMedicalFormData,
        });
      } else if (multiStepFormData?.medicalInformationFormData) {
        const newMedicalFormData = {
          ...multiStepFormData?.medicalInformationFormData,
          dailyInsulinDose: dailyInsulinDose,
        };

        setMultiStepFormData({
          insuranceInformationFormData: {
            ...baseData,
          },
          medicalInformationFormData: newMedicalFormData,
        });
      }
    }

    goToNextFormStep();
  };

  useEffect(() => {
    if (multiStepFormData?.medicalInformationFormData) {
      reset({
        dailyInsulinDose:
          multiStepFormData.medicalInformationFormData.dailyInsulinDose,
      });
    }

    if (!multiStepFormData?.insuranceInformationFormData) return;
    const { insuranceInformationFormData } = multiStepFormData;

    if (
      insuranceInformationFormData.hasSupplementaryPolicy === YES_NO_OPTIONS.YES
    ) {
      reset({
        hasSupplementaryPolicy: YES_NO_OPTIONS.YES,
        supplementaryPolicyName:
          insuranceInformationFormData.supplementaryPolicyName,
        coInsurancePercentage:
          insuranceInformationFormData.coInsurancePercentage,
        supplementaryPolicyNumber:
          insuranceInformationFormData.supplementaryPolicyNumber,
      });
    } else {
      reset({
        hasSupplementaryPolicy: YES_NO_OPTIONS.NO,
      });
    }
  }, [multiStepFormData, reset]);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Shield className="text-primary-600" size={16} />
          <p className="font-extrabold">Insurance information</p>
        </div>
        <p className="text-sm font-extrabold text-neutral-500">
          All fields are required
        </p>
      </div>
      <div className="grid grid-cols-2 gap-10">
        {!insurances?.primaryPayer && !isLoading && <InsuranceErrorBanner />}
        {isLoading && <InsuranceCardsSkeleton />}
        {insurances?.primaryPayer && (
          <InsuranceCard
            key={insurances.primaryPayer + insurances.mbi}
            firstLabel={"Primary"}
            firstLabelValue={insurances.primaryPayer}
            secondLabel="ID"
            secondLabelValue={insurances.mbi}
            planType={getPlanType(insurances.hmo, insurances.ppo)}
          />
        )}
        {insurances?.secondaryPayer && (
          <InsuranceCard
            key={insurances.secondaryPayer + insurances.secondaryPayerId}
            firstLabel={"Secondary"}
            firstLabelValue={insurances.secondaryPayer}
            secondLabel="ID"
            secondLabelValue={insurances.secondaryPayerId}
          />
        )}
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField
          label={"Has Supplementary Policy?"}
          error={errors.hasSupplementaryPolicy?.message}
        >
          <ControlledRadioGroupCard
            name="hasSupplementaryPolicy"
            control={control}
            options={YES_NO_RADIO_OPTIONS}
          />
        </FormField>
        {hasPolicyForm(hasSupplementaryPolicy, errors) && (
          <FormField
            label="Policy name"
            error={errors.supplementaryPolicyName?.message}
          >
            <Input
              placeholder="E.g. Aetna Medicare Supplement"
              {...register("supplementaryPolicyName")}
              error={errors.supplementaryPolicyName?.message}
            />
          </FormField>
        )}
        {hasPolicyForm(hasSupplementaryPolicy, errors) && (
          <FormField
            label="Policy number"
            error={errors.supplementaryPolicyNumber?.message}
          >
            <Input
              placeholder="E.g. 1234567890"
              {...register("supplementaryPolicyNumber")}
              error={errors.supplementaryPolicyNumber?.message}
            />
          </FormField>
        )}
        {hasPolicyForm(hasSupplementaryPolicy, errors) && (
          <FormField
            label="Co-Insurance %"
            error={errors.coInsurancePercentage?.message}
          >
            <Input
              placeholder="E.g. 20%"
              {...register("coInsurancePercentage")}
              error={errors.coInsurancePercentage?.message}
            />
          </FormField>
        )}
        {insurances?.primaryPayer.toLowerCase().includes("aetna") &&
          multiStepFormData?.medicalInformationFormData?.isTakingInsulin ==
            YES_NO_OPTIONS.YES && (
            <FormField
              label="How many times a day do you inject insulin?"
              error={errors.dailyInsulinDose?.message}
            >
              <Controller
                control={control}
                name="dailyInsulinDose"
                render={({ field, fieldState }) => (
                  <SelectGroup>
                    <Select
                      required
                      value={field.value ? String(field.value) : undefined}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <SelectTrigger
                        placeholder="Select the option that applies"
                        className={
                          fieldState.error
                            ? "border-error-500"
                            : "border-neutral-200"
                        }
                      />
                      <SelectContent>
                        {INSULIN_DAILY_DOSE.map((value) => (
                          <SelectItem key={value} value={value}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectGroup>
                )}
              />
            </FormField>
          )}
        <FormButtons isNextDisabled={isLoading || !insurances?.primaryPayer} />
      </form>
    </div>
  );
};
